import React from 'react';
import Table from '../../components/common/Table';
import { NoteDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge } from '../../utils/displayUtils';
import FileIcons from '../common/FileIcons';
import createNoteActions from '../actionMenus/NoteActions';

interface NoteTableProps {
  data: NoteDTO[];
  handleEdit: (row: NoteDTO) => void;
  handleDelete: (row: NoteDTO) => Promise<void>;
  handleResolve?: (row: NoteDTO) => Promise<void>;
  handleViewMember?: (row: NoteDTO) => void;
  handleImpersonateMemberLogin?: (row: NoteDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const NoteTable: React.FC<NoteTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  handleResolve,
  handleViewMember,
  handleImpersonateMemberLogin,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  return (
    <Table
      columns={[
        {
          label: 'Type',
          key: 'noteTypeId',
          render: (row: NoteDTO) => <span>{row.noteType?.title}</span>,
        },

        {
          label: 'Reference',
          key: 'reference',
          render: (row: NoteDTO) => <span>{row.reference ? row.reference : '-'}</span>,
        },

        {
          label: 'Status',
          key: 'isResolved',
          render: (row: NoteDTO) =>
            renderBooleanBadge(row.isResolved ?? false, 'Resolved', 'Open', 'warning'),
        },

        {
          label: 'Component',
          key: 'componentId',
          render: (row: NoteDTO) => <span>{row.component?.title}</span>,
        },

        {
          label: 'Assigned',
          key: 'assignedAdminId',
          render: (row: NoteDTO) => (
            <span>
              {row.assignedAdmin
                ? `${row.assignedAdmin.firstName} ${row.assignedAdmin.lastName}`
                : '-'}
            </span>
          ),
        },

        {
          label: 'Author',
          key: 'createdBy',
          render: (row: NoteDTO) => (
            <span>
              {row.createdByUser?.firstName} {row.createdByUser?.lastName}
            </span>
          ),
        },

        {
          label: 'Member',
          key: 'memberId',
          render: (row: NoteDTO) => (
            <span>
              {row.member?.firstName} {row.member?.lastName}
            </span>
          ),
        },

        {
          label: 'Description',
          key: 'description',
        },

        {
          label: 'File',
          key: 'file',
          render: (row: NoteDTO) => (
            <FileIcons
              files={[
                {
                  id: row.fileId,
                  label: 'Note File',
                },
              ]}
            />
          ),
        },
        {
          label: 'Created',
          key: 'createdUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
        {
          label: 'Updated',
          key: 'updatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
      ]}
      data={data}
      actions={{
        onEdit: handleEdit,
        extra: (row) =>
          createNoteActions({
            onDelete: handleDelete,
            onResolve: handleResolve,
            onViewMember: handleViewMember,
            onImpersonateMemberLogin: handleImpersonateMemberLogin,
          })(row),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
    />
  );
};

export default NoteTable;
